import React from "react";
import { Box, darken, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  Tooltip,
  Customized,
} from "recharts";
import { DashboardCardStyle } from "../../../Dashboard/DashboardContent/style";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#FFF",
        padding: "8px 12px",
        borderRadius: "6px",
        border: "1px solid #E5E7EB",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "2px",
            backgroundColor: "#AC94FA",
          }}
        />
        <Typography variant="caption" fontWeight={600} color="text.primary">
          Age Group: {label}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {payload[0].value} Participants
      </Typography>
    </Box>
  );
};

const EmptyMessage = () => {
  return (
    <g>
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        style={{ fontSize: "16px", fill: "#888" }}
      >
        No Data
      </text>
    </g>
  );
};

const AgeGroupGraph = ({ data }: any) => {
  const {
    yMin = 0,
    yMax = 50,
    title = "Participants by Age Group",
  } = data || {};

  return (
    <Box sx={DashboardCardStyle}>
      <Typography variant="subtitle1" fontWeight="medium" color="text.primary">
        {title}
      </Typography>
      <Box sx={{ height: "360px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data?.data}
            margin={{
              top: 20,
              right: 30,
              bottom: 35,
              left: 20,
            }}
          >
            <defs>
              <linearGradient
                id="ageGroupBarGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#AC94FA" stopOpacity={0.9} />
                <stop offset="100%" stopColor="#AC94FA" stopOpacity={0.2} />
              </linearGradient>
              <linearGradient
                id="ageGroupBarGradientActive"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={darken("#AC94FA", 0.15)}
                  stopOpacity={0.95}
                />
                <stop
                  offset="100%"
                  stopColor={darken("#AC94FA", 0.15)}
                  stopOpacity={0.3}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              stroke="#d3d3d3"
              strokeDasharray="5 5"
              strokeOpacity={0.8}
              vertical={false}
            />
            <XAxis
              dataKey={"group"}
              tick={{ fill: "#355962", fontSize: 14, fontWeight: 500 }}
              stroke="#e0e3eb"
              label={{
                value: "Age Groups",
                position: "insideBottom",
                offset: -20,
                style: { fontSize: 14 },
              }}
            />
            <YAxis
              domain={[yMin, yMax]}
              type="number"
              minTickGap={10}
              ticks={Array.from(
                { length: (yMax - yMin) / 10 + 1 },
                (_, i) => yMin + i * 10
              )}
              orientation="left"
              tick={{ fill: "#355962", fontSize: 14, fontWeight: 500 }}
              stroke="#e0e3eb"
              label={{
                value: "Count",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 14, fontWeight: 500, color: "#9CA3AF" },
              }}
            />
            <Bar
              dataKey="count"
              fill="url(#ageGroupBarGradient)"
              radius={[10, 10, 0, 0]}
              barSize={48}
              activeBar={{
                fill: "url(#ageGroupBarGradientActive)",
              }}
            />
            {data?.data?.length === 0 && (
              <Customized component={EmptyMessage} />
            )}
            <Tooltip content={<CustomTooltip />} cursor={false} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default AgeGroupGraph;
